import { Component, OnInit } from '@angular/core';
import { AppService } from '../../services/app.service';
import { of } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { IUserExternalRole } from '../../models/User';
import { tap } from 'rxjs/operators';

const cache: {
    roles?: IUserExternalRole[],
    userName?: string
} = {};

interface ITypeOption {
    name: string;
    total: number;
}

interface ISortOption {
    key: SortKey;
    name: string;
}

type SortKey = 'res-asc' | 'res-desc';

@Component({
    selector: 'app-my-roles',
    templateUrl: './my-roles.component.html',
    styleUrls: ['./my-roles.component.scss']
})
export class MyRolesComponent implements OnInit {
    constructor(
        private app: AppService,
        private auth: AuthService,
        private service: UserService
    ) { }

    items: IUserExternalRole[] = [];
    typeOptions: ITypeOption[] = [];
    readonly sortOptions: ISortOption[] = [
        { key: 'res-asc', name: this.app.translate('myRoles_sortResourceAsc') },
        { key: 'res-desc', name: this.app.translate('myRoles_sortResourceDesc') }
    ];
    filterTypes: ITypeOption[] = [];
    filterTerm?: string;
    sortKey: SortKey = 'res-asc';
    isReady: boolean;

    readonly typeDisplayFn = (t: ITypeOption) => `${t.name} (${t.total})`;

    get isEmpty(): boolean {
        return !this.all.length;
    }

    private all: IUserExternalRole[] = [];

    ngOnInit() {
        this.auth.user.subscribe(user => {
            if (user) {
                if (cache.userName != user.username) {
                    cache.roles = undefined;
                    cache.userName = user.username;
                }

                this.fetch().subscribe(data => {
                    this.all = data;
                    this.items = data;
                    this.sort();

                    this.typeOptions = data.reduce((acc, r) => {
                        const t = acc.find(t => t.name == r.Type);
                        if (t) {
                            t.total++;
                        } else {
                            acc.push({ name: r.Type, total: 1 });
                        }
                        return acc;
                    }, []);

                    this.isReady = true;
                });
            } else {
                cache.roles = undefined;
                cache.userName = undefined;
            }
        });
    }

    filter() {
        const term = this.filterTerm?.toLowerCase();
        const types = this.filterTypes.map(t => t.name);

        if (!term && !types.length) {
            this.items = this.all;
        } else {
            this.items = this.all.filter(t =>
                (!types.length || types.includes(t.Type))
                && (!term || (t.Resource?.toLowerCase().includes(term)
                    || t.Name?.toLowerCase().includes(term)
                    || t.Description?.toLowerCase().includes(term)))
            );
        }
    }

    sort() {
        switch (this.sortKey) {
            case 'res-asc':
                this.items = this.items.sort((a, b) => a.Resource.localeCompare(b.Resource, this.app.currentLanguage));
                break;

            case 'res-desc':
                this.items = this.items.sort((a, b) => -a.Resource.localeCompare(b.Resource, this.app.currentLanguage));
                break;
        }
    }

    private fetch() {
        if (cache.roles) {
            return of(cache.roles);
        } else {
            return this.app.addLoading(this.service.getMyExternalRoles()).pipe(tap(data => {
                cache.roles = data;
            }));
        }
    }
}
